import React, {useState, useEffect} from 'react'
import {handleError} from "../common/utils";
import ButtonLoading from "../components/ButtonLoading";
import {isDecimal} from "../utils/number";
import agent from "../api/agent";

const StripeInvoiceSummary = ({invoiceData, setInvoiceUpdate, paymentIntentId, allowCheckout, actionsLocked = false}) => {
  const [creditLoading, setCreditLoading] = useState(false)
  const [couponLoading, setCouponLoading] = useState(false)
  const [balance, setBalance] = useState('')
  const [invoiceId, setInvoiceId] = useState('')
  const [lineItems, setLineItems] = useState([])
  const [coupon, setCoupon] = useState({})
  const [credits, setCredits] = useState({})
  const [creditApplyPending, setCreditApplyPending] = useState(0)
  const [creditApplyInput, setCreditApplyInput] = useState({creditApplyInput: '', creditMessage: ''})
  const [creditApplyPendingBalance, setCreditApplyPendingBalance] = useState(0)
  const [couponCode, setCouponCode] = useState({})

  useEffect(() => {
    setBalance(invoiceData.balance)
    setCoupon(invoiceData.coupon)
    setCredits(invoiceData.credits)
    setLineItems(invoiceData.lineItems)
    setInvoiceId(invoiceData.invoiceId)
  }, [invoiceData])

  const doTryCredit = async () => {
    if (actionsLocked) {
      return
    }
    if (!creditApplyInput.creditApplyInput) {
      setCreditApplyInput({creditMessage: 'Please enter an amount'})
    }

    if (!isDecimal(('' + creditApplyInput.creditApplyInput), {decimal_digits: '0,2'})) {
      setCreditApplyInput({creditMessage: 'Please enter a valid amount'})
    }

    if (creditApplyInput.creditApplyInput * 1 < 0) {
      setCreditApplyInput({creditMessage: 'Please enter a valid amount'})
    }

    if (creditApplyInput.creditApplyInput * 1 > credits?.balance) {
      setCreditApplyInput({creditMessage: 'Insuffucient credit balance'})
    }

    try {
      setCreditLoading(true)

      const postData = {
        invoiceId: invoiceId,
        useCredit: creditApplyInput.creditApplyInput,
        paymentIntentId: paymentIntentId
      }
      const response = await agent.checkout.useCredit(postData)
      const updateResponse = response.data.data
      setInvoiceUpdate(updateResponse)
      setCredits(updateResponse.credits)
      setBalance(updateResponse.balance)
      setLineItems(updateResponse.lineItems)
      setCoupon(updateResponse.coupon)

      if (updateResponse.credits.applied.length) {
        setCreditApplyPending(updateResponse.credits.applied[0].amount)
        setCreditApplyInput({creditApplyInput: updateResponse.credits.applied[0].amount})
        setCreditApplyPendingBalance(updateResponse.credits.balance - updateResponse.credits.applied[0].amount)
      } else {
        setCreditApplyPending(0)
        setCreditApplyInput({creditApplyInput: 0})
        setCreditApplyPendingBalance(updateResponse.credits.balance)
      }

    } catch (e) {
      setCreditApplyInput({creditMessage: e.message})
      handleError(e)
    } finally {
      setCreditLoading(false)
    }
  }

  const doTryCoupon = async () => {
    if (actionsLocked) {
      return
    }
    let inputCouponCode = couponCode.couponCode.replace(/[^A-Za-z0-9-]/g, '').slice(0, 45).toUpperCase()
    if (!inputCouponCode) {
      setCouponCode({couponMessage: 'Please enter a valid coupon/promo code'})
    }

    try {
      setCouponLoading(true)
      let postData = {
        invoiceId: invoiceId,
        couponCode: couponCode.couponCode,
        paymentIntentId: paymentIntentId
      }
      const response = await agent.checkout.useCoupon(postData)
      const couponResponse = response.data.data

      setInvoiceUpdate(couponResponse)
      setCoupon(couponResponse.coupon)
      setCredits(couponResponse.credits)
      setBalance(couponResponse.balance)
      setLineItems(couponResponse.lineItems)

    } catch (e) {
      setCouponCode({couponMessage: e.message})
      handleError(e)
    } finally {
      setCouponLoading(false)
    }
  }

  const prepaidDutyNames = ['Import Duties', 'Import Taxes', 'Import Services Taxes']
  const dutiesPrepaid = lineItems.some(item =>
    prepaidDutyNames.includes(item.itemName) || item.itemName?.startsWith('EU customs duty')
  )

  return (
    <div className="stripe-checkout-content">
      <h4 className="stripe-checkout-right-title">Invoice Summary</h4>
      {dutiesPrepaid &&
        <div className="ddp-paid-notice" role="note">
          <div className="ddp-paid-notice-title">Delivered Duty Paid</div>
          <div className="ddp-paid-notice-body">Duties and taxes are paid with this invoice. You will not pay them again when the package is delivered.</div>
        </div>
      }
      <div className="srr-invoice-grid">
        {lineItems.length && lineItems.map((item,index) => {
          return (
            <div className="srr-invoice-line" key={index}>
              <div className="srr-invoice-line-label">{item.itemName}
                <div>{item.packageId ? `(#${item.packageId?.toString().padStart(6, '0')})` : ''}</div>
                {item.itemName?.startsWith('EU customs duty') &&
                  <a className="eu-duty-explain" href="https://taxation-customs.ec.europa.eu/news/guidance-and-legal-text-temporary-flat-fee-low-value-imports-which-will-apply-until-1-july-2028-2026-06-08_en" target="_blank" rel="noopener noreferrer">Official EU explanation</a>
                }
              </div>
              <div className="srr-invoice-line-value">{!item.waived ? '$' + item.amount.toFixed(2) : 'Free'}</div>
            </div>
          )
        })}
      </div>

      {allowCheckout && <>
        <div className="srr-invoice-line-hr">&nbsp;</div>
        <div className="srr-coupon">
          <div className="srr-coupon-label" style={{fontWeight: 'bold'}}>Coupon/Promo Code</div>
          <div className="srr-coupon-input">
            <input className="srr-coupon-input-actual form-control" type="text" value={couponCode.couponCode}
                   disabled={actionsLocked}
                   onChange={(e) => setCouponCode({couponCode: e.target.value, couponMessage: ''})}/>
            {!!couponLoading ?
              <div className="btn btn-sm btn-primary srr-coupon-button"><ButtonLoading/></div> :
              <div className={`btn btn-primary btn-sm srr-coupon-button${actionsLocked ? ' disabled' : ''}`} onClick={actionsLocked ? undefined : doTryCoupon}>Apply</div>}
          </div>
          {!!couponCode.couponMessage && <div className="srr-coupon-message">{couponCode.couponMessage}</div>}
        </div>
        {!!coupon.applied?.length &&
          <div className="srr-invoice-line">
            <div className="srr-invoice-line-label">{coupon.applied[0].itemName}</div>
            <div className="srr-invoice-line-value">$ -{coupon.applied[0].amount.toFixed(2)}</div>
          </div>
        }
      </>}

      {!!credits?.balance && allowCheckout &&
        <>
          <div className="srr-invoice-line-hr">&nbsp;</div>
          <div className="srl-credit">
            <div className="srl-credit-label" style={{fontWeight: 'bold'}}>Use Reship Credits</div>
            <div className="srl-credit-action">
              <input className="srl-credit-input form-control" disabled={credits?.balance < 0 || actionsLocked} type="text"
                     value={creditApplyInput.creditApplyInput}
                     onChange={(e) => setCreditApplyInput({creditApplyInput: e.target.value, creditMessage: ''})}/>
              {credits?.balance >= 0 && (
                creditLoading ?
                  <div className="btn btn-primary btn-sm srl-credit-button"><ButtonLoading/></div>
                  :
                  <div className={`btn btn-primary btn-sm srl-credit-button${actionsLocked ? ' disabled' : ''}`}
                       onClick={actionsLocked ? undefined : doTryCredit}>{creditApplyPending ? 'Update' : 'Apply'}</div>
              )}
            </div>

            {!!creditApplyInput.creditMessage && <div className="srl-credit-message">{creditApplyInput.creditMessage}</div>}

            {!!creditApplyPending ?
              <div className="srl-credit-info">Using ${creditApplyPending.toFixed(2)}, remaining balance:
                ${creditApplyPendingBalance.toFixed(2)}</div>
              :
              <div className="srl-credit-info">Current balance: ${credits?.balance.toFixed(2)}</div>
            }
          </div>

          {!!credits.applied.length &&
            <div>
              {credits.applied.map((credit, idx) => {
                return (
                  <div className="srr-invoice-line" key={'credit-' + idx}>
                    <div className="srr-invoice-line-label">{credit.itemName}</div>
                    <div className="srr-invoice-line-value">$ -{credit.amount.toFixed(2)}</div>
                  </div>
                )
              })}
            </div>
          }
        </>
      }

      <div className="srr-invoice-line-hr">&nbsp;</div>
      <div className="srr-invoice-line srr-invoice-total">
        <div className="srr-invoice-line-label font-weight-600">Invoice Total</div>
        <div className="srr-invoice-line-value font-weight-600">$ {balance && balance.toFixed(2)}</div>
      </div>
      <div className="srr-currency">USD</div>
      <div className="srr-invoice-line-hr">&nbsp;</div>
      {credits?.balance < 0 &&
        <div className="error-text small">Sorry, your account has a hold on it due to an outstanding
          negative balance.<br/><br/>Please contact <a href="mailto:accounts@reship.com"
                                                       target="blank">accounts@reship.com</a> to resolve this issue,
          thank you.</div>
      }
    </div>
  )


}

export default StripeInvoiceSummary